import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type TooltipType,
    Tooltip,
    Button,
    Text,
    Kbd
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    HeartIcon,
    CheckIcon,
    InfoIcon,
    StarIcon,
    BellIcon
} from "../../anatomy/icons";

const NAME = "Tooltip";

const USAGE_CODE = `<Tooltip
    title="Add to favourites"
    side="top"
>
    <Button
        accessibilityLabel="Add to favourites"
        onPress={() => undefined}
        variant="tonal"
        icon={HeartIcon}
        type="neutral"
    />
</Tooltip>`;

const ACTIONS_CODE = `<Tooltip
    action={{
        onPress: ({ closeAnimation }) => closeAnimation(),
        title: "Try"
    }}
    secondaryAction={{
        onPress: ({ closeAnimation }) => closeAnimation(),
        title: "Later"
    }}
    subTitle="Search from any page with Ctrl + K."
    isShowCloseButton={true}
    title="Quick search"
    triggerMode="click"
>
    <Button
        onPress={() => undefined}
        title="Search"
    />
</Tooltip>`;

const DELAYS_CODE = `<Tooltip.Provider
    closeDelay={150}
    openDelay={400}
    skipDelay={500}
>
    <Tooltip title="Add to favourites">
        <Button
            accessibilityLabel="Add to favourites"
            onPress={() => undefined}
            variant="tonal"
            icon={HeartIcon}
            type="neutral"
        />
    </Tooltip>
    <Tooltip title="Star">
        <Button
            accessibilityLabel="Star"
            onPress={() => undefined}
            variant="tonal"
            icon={StarIcon}
            type="neutral"
        />
    </Tooltip>
    <Tooltip
        title="Notifications"
        openDelay={700}
    >
        <Button
            accessibilityLabel="Notifications"
            onPress={() => undefined}
            variant="tonal"
            icon={BellIcon}
            type="neutral"
        />
    </Tooltip>
</Tooltip.Provider>`;

const SHORTCUT_CODE = `<Tooltip
    content={() => <>
        <Text
            variant="labelSmallSize"
            color="contrastHigh"
        >
            Save
        </Text>
        <Kbd
            displayBehaviour="always"
            isInverse={true}
            keys="mod+S"
        />
    </>}
>
    <Button
        onPress={() => undefined}
        title="Save"
    />
</Tooltip>`;

const SIDES = [
    "top",
    "right",
    "bottom",
    "left"
] as const;

const ALIGNS = [
    "start",
    "center",
    "end"
] as const;

const TYPES: Array<TooltipType> = [
    "neutral",
    "primary",
    "success",
    "warning",
    "danger",
    "info"
];

const COPIED_DELAY = 1600;

type TooltipActionKey = "tooltipPage.later" | "tooltipPage.try";

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("tooltipPage.placementTitle"),
        id: "placement"
    },
    {
        title: t("tooltipPage.contentTitle"),
        id: "content"
    },
    {
        title: t("tooltipPage.triggerTitle"),
        id: "trigger"
    },
    {
        title: t("tooltipPage.delaysTitle"),
        id: "delays"
    },
    {
        title: t("tooltipPage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TooltipPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isCopied,
        setIsCopied
    ] = useState(false);

    const [
        lastActionKey,
        setLastActionKey
    ] = useState<TooltipActionKey | null>(null);

    const toolbarItems = [
        {
            title: t("tooltipPage.favourite"),
            icon: HeartIcon
        },
        {
            title: t("tooltipPage.delayStar"),
            icon: StarIcon
        },
        {
            title: t("tooltipPage.delayNotifications"),
            icon: BellIcon
        },
        {
            title: t("sample.search"),
            icon: SearchIcon
        }
    ];

    const renderShortcutContent = () => <>
        <Text
            variant="labelSmallSize"
            color="contrastHigh"
        >
            {t("tooltipPage.save")}
        </Text>
        <Kbd
            displayBehaviour="always"
            isInverse={true}
            keys="mod+S"
        />
    </>;

    const renderCustomContent = () => <Text
        variant="labelSmallSize"
        color="contrastHigh"
    >
        {t("tooltipPage.customContent")}
    </Text>;

    useEffect(() => {
        if(!isCopied) {
            return;
        }

        const timer = window.setTimeout(() => setIsCopied(false), COPIED_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isCopied
    ]);

    return <Article>
        <ComponentHeader
            lead={t("tooltipPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("tooltipPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("tooltipPage.overviewCaption")}
            >
                <Tooltip
                    title={t("tooltipPage.favourite")}
                    side="top"
                >
                    <Button
                        accessibilityLabel={t("tooltipPage.favourite")}
                        onPress={() => undefined}
                        icon={HeartIcon}
                        variant="tonal"
                        type="neutral"
                    />
                </Tooltip>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("tooltipPage.placementTitle")}
            id="placement"
        >
            <Paragraph>
                {t("tooltipPage.placementText")}
            </Paragraph>
            <Preview
                caption={t("tooltipPage.sideCaption")}
            >
                {
                    SIDES.map((side) => <Tooltip
                        title={`side="${side}"`}
                        side={side}
                        key={side}
                    >
                        <Button
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                            title={side}
                            size="small"
                        />
                    </Tooltip>)
                }
            </Preview>
            <Preview
                caption={t("tooltipPage.alignCaption")}
            >
                {
                    ALIGNS.map((align) => <Tooltip
                        title={`align="${align}"`}
                        align={align}
                        side="bottom"
                        key={align}
                    >
                        <Button
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                            title={align}
                            size="small"
                        />
                    </Tooltip>)
                }
            </Preview>
        </Section>
        <Section
            title={t("tooltipPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("tooltipPage.contentText")}
            </Paragraph>
            <Preview>
                <Tooltip
                    title={t("tooltipPage.copyLink")}
                >
                    <Button
                        onPress={() => undefined}
                        variant="outline"
                        type="neutral"
                        title="title"
                        size="small"
                    />
                </Tooltip>
                <Tooltip
                    subTitle={t("tooltipPage.lastUpdateTime")}
                    title={t("tooltipPage.lastUpdate")}
                >
                    <Button
                        onPress={() => undefined}
                        variant="outline"
                        title="subTitle"
                        type="neutral"
                        size="small"
                    />
                </Tooltip>
                <Tooltip
                    content={renderCustomContent}
                >
                    <Button
                        onPress={() => undefined}
                        variant="outline"
                        title="content"
                        type="neutral"
                        size="small"
                    />
                </Tooltip>
            </Preview>
            <Preview
                caption={t("tooltipPage.typeCaption")}
            >
                {
                    TYPES.map((type) => <Tooltip
                        icon={InfoIcon}
                        title={type}
                        type={type}
                        key={type}
                    >
                        <Button
                            accessibilityLabel={type}
                            onPress={() => undefined}
                            icon={InfoIcon}
                            variant="tonal"
                            type="neutral"
                        />
                    </Tooltip>)
                }
            </Preview>
            <Paragraph>
                {t("tooltipPage.shortcutText")}
            </Paragraph>
            <Preview
                caption={t("tooltipPage.shortcutCaption")}
            >
                <Tooltip
                    content={renderShortcutContent}
                >
                    <Button
                        title={t("tooltipPage.save")}
                        onPress={() => undefined}
                        variant="outline"
                        type="neutral"
                    />
                </Tooltip>
            </Preview>
            <CodeBlock
                code={SHORTCUT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("tooltipPage.triggerTitle")}
            id="trigger"
        >
            <Paragraph>
                {t("tooltipPage.triggerText")}
            </Paragraph>
            <Preview>
                <Tooltip
                    title={t("tooltipPage.hoverTooltip")}
                >
                    <Button
                        onPress={() => undefined}
                        variant="outline"
                        type="neutral"
                        title="hover"
                    />
                </Tooltip>
                <Tooltip
                    title={t("tooltipPage.clickTooltip")}
                    triggerMode="click"
                >
                    <Button
                        onPress={() => undefined}
                        variant="outline"
                        type="neutral"
                        title="click"
                    />
                </Tooltip>
                <Tooltip
                    title={t("tooltipPage.pressTooltip")}
                    isCloseOnPress={true}
                    triggerMode="click"
                >
                    <Button
                        onPress={() => undefined}
                        title="isCloseOnPress"
                        variant="outline"
                        type="neutral"
                    />
                </Tooltip>
            </Preview>
            <Paragraph>
                {t("tooltipPage.controlledText")}
            </Paragraph>
            <Preview
                caption={t("tooltipPage.controlledCaption")}
            >
                <Tooltip
                    title={t("tooltipPage.copied")}
                    isOpen={isCopied}
                    icon={CheckIcon}
                    type="success"
                >
                    <Button
                        onPress={() => setIsCopied(true)}
                        title={t("tooltipPage.copyLink")}
                        variant="outline"
                        type="neutral"
                    />
                </Tooltip>
            </Preview>
        </Section>
        <Section
            title={t("tooltipPage.delaysTitle")}
            id="delays"
        >
            <Paragraph>
                {t("tooltipPage.delaysText")}
            </Paragraph>
            <Preview
                caption={t("tooltipPage.delaysCaption")}
            >
                {
                    toolbarItems.map((item) => <Tooltip
                        title={item.title}
                        key={item.title}
                        side="top"
                    >
                        <Button
                            accessibilityLabel={item.title}
                            onPress={() => undefined}
                            icon={item.icon}
                            variant="tonal"
                            type="neutral"
                        />
                    </Tooltip>)
                }
            </Preview>
            <Preview
                caption={t("tooltipPage.slowCaption")}
            >
                <Tooltip
                    title={t("tooltipPage.slowTooltip")}
                    openDelay={700}
                >
                    <Button
                        onPress={() => undefined}
                        title="openDelay={700}"
                        variant="outline"
                        type="neutral"
                    />
                </Tooltip>
            </Preview>
            <Paragraph>
                <InlineCode>
                    Tooltip.Provider
                </InlineCode>
                {" "}
                {t("tooltipPage.providerText")}
            </Paragraph>
            <CodeBlock
                code={DELAYS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("tooltipPage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("tooltipPage.actionsText")}
            </Paragraph>
            <Preview
                caption={t("tooltipPage.actionsCaption", [
                    lastActionKey ? t(lastActionKey) : "—"
                ])}
            >
                <Tooltip
                    secondaryAction={{
                        onPress: () => setLastActionKey("tooltipPage.later"),
                        title: t("tooltipPage.later")
                    }}
                    action={{
                        onPress: () => setLastActionKey("tooltipPage.try"),
                        title: t("tooltipPage.try")
                    }}
                    subTitle={t("tooltipPage.quickSearchHint")}
                    title={t("tooltipPage.quickSearch")}
                    isShowCloseButton={true}
                    triggerMode="click"
                >
                    <Button
                        title={t("sample.search")}
                        onPress={() => undefined}
                    />
                </Tooltip>
            </Preview>
            <CodeBlock
                code={ACTIONS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("tooltipPage.a11yRole"),
                    t("tooltipPage.a11yName"),
                    t("tooltipPage.a11yOpen"),
                    t("tooltipPage.a11yHoverable"),
                    t("tooltipPage.a11yAnchor"),
                    t("tooltipPage.a11yDialog"),
                    t("tooltipPage.a11yTouch"),
                    t("tooltipPage.a11yWarning"),
                    t("tooltipPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("tooltipPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("tooltipPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("tooltipPage.propContent"),
                        type: "ReactNode | () => ReactNode",
                        name: "content"
                    },
                    {
                        description: t("tooltipPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "\"primary\" | \"neutral\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("tooltipPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"top\" | \"right\" | \"bottom\" | \"left\"",
                        description: t("tooltipPage.propSide"),
                        defaultValue: "\"top\"",
                        name: "side"
                    },
                    {
                        type: "\"start\" | \"center\" | \"end\"",
                        description: t("tooltipPage.propAlign"),
                        defaultValue: "\"center\"",
                        name: "align"
                    },
                    {
                        description: t("tooltipPage.propTriggerMode"),
                        type: "\"hover\" | \"click\"",
                        defaultValue: "\"hover\"",
                        name: "triggerMode"
                    },
                    {
                        description: t("tooltipPage.propIsCloseOnPress"),
                        name: "isCloseOnPress",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "{ title, icon, onPress({ closeAnimation }) }",
                        description: t("tooltipPage.propAction"),
                        name: "action"
                    },
                    {
                        type: "{ title, icon, onPress({ closeAnimation }) }",
                        description: t("tooltipPage.propSecondaryAction"),
                        name: "secondaryAction"
                    },
                    {
                        description: t("tooltipPage.propIsShowCloseButton"),
                        name: "isShowCloseButton",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("tooltipPage.propIsCloseOnPressActionButton"),
                        name: "isCloseOnPressActionButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("tooltipPage.propSizing"),
                        type: "\"content\" | \"fixed\"",
                        defaultValue: "\"content\"",
                        name: "sizing"
                    },
                    {
                        description: t("tooltipPage.propWidth"),
                        name: "width",
                        type: "number"
                    },
                    {
                        description: t("tooltipPage.propHeight"),
                        name: "height",
                        type: "number"
                    },
                    {
                        description: t("tooltipPage.propOpenDelay"),
                        defaultValue: "200",
                        name: "openDelay",
                        type: "number"
                    },
                    {
                        description: t("tooltipPage.propCloseDelay"),
                        defaultValue: "300",
                        name: "closeDelay",
                        type: "number"
                    },
                    {
                        description: t("tooltipPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("tooltipPage.propIsDefaultOpen"),
                        defaultValue: "false",
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("tooltipPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("tooltipPage.propOnClosed"),
                        type: "() => void",
                        name: "onClosed"
                    },
                    {
                        description: t("tooltipPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("tooltipPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("tooltipPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tooltipPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Tooltip.Provider
                </InlineCode>
                {" "}
                {t("tooltipPage.providerPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tooltipPage.propProviderOpenDelay"),
                        name: "openDelay",
                        type: "number"
                    },
                    {
                        description: t("tooltipPage.propProviderCloseDelay"),
                        name: "closeDelay",
                        type: "number"
                    },
                    {
                        description: t("tooltipPage.propProviderSkipDelay"),
                        defaultValue: "300",
                        name: "skipDelay",
                        type: "number"
                    },
                    {
                        description: t("tooltipPage.propProviderChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TooltipPage;
